import {
  captionsToSrt,
  captionsToVtt,
  type ExportFormat,
  type ExportInfo,
  formatBytes,
  formatTimecode,
  getPrintProvider,
  hasMixedSizes,
  maxPageEdge,
  PDFX_LABELS,
  PDFX_STANDARDS,
  type PdfxStandard,
  type PreflightIssue,
  pageSize,
  parseHex,
  preflightDocument,
  resolvePrintProfile,
  videoDuration,
  videoExportSize,
} from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Download,
  FileText,
  Film,
  Image,
  Info,
  Loader2,
  Printer,
  XCircle,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/card";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Select } from "../components/ui/input";
import { orpc } from "../lib/api";
import { useOrgRole } from "../lib/org";
import { cn, errorMessage, formatRelative } from "../lib/utils";
import { useEnv } from "./env";
import { PrintSettings } from "./PropertiesPanel";
import { useEditor, useEditorApi } from "./store";
import { Segmented, Toggle } from "./ui";
import { useCaptions } from "./video/data";

const FORMAT_OPTIONS: {
  value: ExportFormat;
  label: string;
  description: string;
  icon: ReactNode;
}[] = [
  { value: "png", label: "PNG", description: "Beste Qualität für Social Media", icon: <Image /> },
  { value: "jpg", label: "JPG", description: "Kleine Datei, ohne Transparenz", icon: <Image /> },
  {
    value: "pdf-print",
    label: "PDF Druck",
    description: "CMYK, Beschnitt, PDF/X",
    icon: <Printer />,
  },
  {
    value: "pdf-digital",
    label: "PDF digital",
    description: "Zum Teilen und Ansehen",
    icon: <FileText />,
  },
];

const ICONS: Record<PreflightIssue["severity"], ReactNode> = {
  error: <XCircle className="size-4 shrink-0 text-red-600" />,
  warning: <AlertTriangle className="size-4 shrink-0 text-amber-500" />,
  info: <Info className="size-4 shrink-0 text-sky-500" />,
};

/** Why the export is off: the role, or no running subscription. */
function NoExportHint({ orgId }: { orgId: string }) {
  const { readOnly } = useOrgRole(orgId);
  return (
    <p className="text-xs text-zinc-500">
      {readOnly
        ? "Ohne laufendes Abo ist Exportieren nicht möglich (Einstellungen → Tarif)."
        : "Betrachter:innen können nicht exportieren."}
    </p>
  );
}

export function IssueList({
  issues,
  onSelect,
}: {
  issues: PreflightIssue[];
  onSelect?: (issue: PreflightIssue) => void;
}) {
  const order = { error: 0, warning: 1, info: 2 };
  const sorted = [...issues].sort((a, b) => order[a.severity] - order[b.severity]);
  if (sorted.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-emerald-700">
        <CheckCircle2 className="size-4" /> Keine Probleme gefunden.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5">
      {sorted.map((issue, index) => (
        <li
          // biome-ignore lint/suspicious/noArrayIndexKey: messages can repeat
          key={index}
          className={cn(
            "flex gap-2 text-sm text-zinc-700",
            issue.elementId && onSelect && "cursor-pointer hover:text-zinc-950",
          )}
          onClick={() => issue.elementId && onSelect?.(issue)}
        >
          {ICONS[issue.severity]}
          <span>{issue.message}</span>
        </li>
      ))}
    </ul>
  );
}

function ExportStatus({ info }: { info: ExportInfo }) {
  if (info.status === "failed") {
    return (
      <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        <p className="flex items-center gap-2 font-medium">
          <AlertCircle className="size-4" /> {info.error ?? "Export fehlgeschlagen"}
        </p>
        {info.report ? (
          <IssueList issues={info.report.issues.filter((issue) => issue.severity === "error")} />
        ) : null}
      </div>
    );
  }
  if (info.status !== "done") {
    return (
      <div className="space-y-2 rounded-lg border border-zinc-200 p-3">
        <p className="flex items-center gap-2 text-sm text-zinc-700">
          <Loader2 className="size-4 animate-spin" />
          {info.status === "queued"
            ? info.queuePosition
              ? `In der Warteschlange (${info.queuePosition} vor dir) …`
              : "Wartet auf den Start …"
            : info.format === "pdf-print"
              ? "Druck-PDF wird erstellt und geprüft …"
              : info.format === "mp4"
                ? `Video wird gerendert … ${info.progress} %`
                : "Wird erstellt …"}
        </p>
        <Progress value={info.progress} />
      </div>
    );
  }
  return (
    <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium text-emerald-900">
            <CheckCircle2 className="size-4" /> Fertig
          </p>
          <p className="truncate text-xs text-emerald-900/70">
            {info.fileName} · {formatBytes(info.sizeBytes ?? 0)}
          </p>
        </div>
        {info.url ? (
          <Button asChild size="sm">
            <a href={info.url} download={info.fileName ?? undefined}>
              <Download /> Herunterladen
            </a>
          </Button>
        ) : null}
      </div>
      {info.report ? (
        <div className="space-y-2">
          <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
            {info.report.checks.map((check) => (
              <li key={check.label} className="flex items-center gap-1.5 text-xs text-zinc-700">
                {check.status === "ok" ? (
                  <CheckCircle2 className="size-3.5 text-emerald-600" />
                ) : check.status === "warning" ? (
                  <AlertTriangle className="size-3.5 text-amber-500" />
                ) : (
                  <XCircle className="size-3.5 text-red-600" />
                )}
                {check.label}
              </li>
            ))}
          </ul>
          {info.report.issues.length > 0 ? <IssueList issues={info.report.issues} /> : null}
        </div>
      ) : null}
    </div>
  );
}

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Saves pending changes, so the server exports the current state. */
  beforeExport: () => Promise<unknown>;
}

/** Exports that finished this recently are still shown when the dialog opens again. */
const SHOW_FINISHED_MS = 15 * 60_000;

const exportActive = (info: ExportInfo) => info.status === "queued" || info.status === "processing";

/**
 * The export the dialog shows: the one just started or – after closing and
 * reopening the dialog – the newest export of this file that is still
 * running or finished a moment ago. A render keeps running on the server
 * when the dialog closes, so it must not disappear from the dialog.
 */
function useCurrentExport(open: boolean, format: ExportFormat, pollMs: number) {
  const env = useEnv();
  const queryClient = useQueryClient();
  const [startedId, setStartedId] = useState<string | null>(null);
  const recent = useQuery({
    ...orpc.exports.list.queryOptions({ input: { orgId: env.orgId, documentId: env.documentId } }),
    enabled: open,
    refetchInterval: (query) => (query.state.data?.some(exportActive) ? 3000 : false),
  });
  const adopted = recent.data?.find(
    (entry) =>
      entry.format === format &&
      (exportActive(entry) ||
        (entry.finishedAt && Date.now() - new Date(entry.finishedAt).getTime() < SHOW_FINISHED_MS)),
  );
  const exportId = startedId ?? adopted?.id ?? null;
  const status = useQuery({
    ...orpc.exports.get.queryOptions({ input: { orgId: env.orgId, exportId: exportId ?? "" } }),
    enabled: open && Boolean(exportId),
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && !exportActive(data) ? false : pollMs;
    },
  });
  const current = status.data?.id === exportId ? status.data : (adopted ?? null);
  return {
    current: exportId ? current : null,
    recent,
    started(id: string) {
      setStartedId(id);
      void recent.refetch();
      // The activity tray shows it right away, not only at its next poll.
      void queryClient.invalidateQueries({ queryKey: orpc.activity.key() });
    },
    /** Closing (or another format) forgets the explicit choice; the newest one shows again. */
    reset() {
      setStartedId(null);
    },
  };
}

export function ExportDialog(props: ExportDialogProps) {
  const kind = useEditor((s) => s.doc.kind);
  return kind === "video" ? <VideoExportDialog {...props} /> : <DesignExportDialog {...props} />;
}

function download(text: string, fileName: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** MP4 export (rendered on the server) and caption files (created right here). */
function VideoExportDialog({ open, onOpenChange, beforeExport }: ExportDialogProps) {
  const env = useEnv();
  const plan = useQuery(orpc.plan.get.queryOptions({ input: { orgId: env.orgId } }));
  const free = plan.data?.plan === "free";
  const doc = useEditor((s) => s.doc);
  const { pages, captionsElement, infos } = useCaptions();
  const [resolution, setResolution] = useState<"1080p" | "720p">("1080p");
  const [quality, setQuality] = useState<"standard" | "high">("standard");
  const [captions, setCaptions] = useState(true);
  const { current, recent, started, reset } = useCurrentExport(open, "mp4", 1500);
  const create = useMutation(
    orpc.exports.create.mutationOptions({
      onSuccess: (info) => started(info.id),
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const duration = videoDuration(doc);
  const recognising = [...infos.values()].some(
    (info) => info?.status === "queued" || info?.status === "processing",
  );
  const base = safeName(env.title);
  const uppercase = captionsElement?.style.uppercase ?? false;
  const sizeLabel = (value: "1080p" | "720p") => {
    const size = videoExportSize(doc.format, value);
    return `${size.width} × ${size.height}`;
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent title="Video exportieren" className="max-w-2xl">
        {free ? (
          <p className="mb-4 text-sm text-zinc-600">
            MP4-Export gibt es ab Solo.{" "}
            <a className="underline" href={`/o/${env.orgId}/settings?tab=plan`}>
              Tarif ansehen
            </a>
          </p>
        ) : null}
        <div className="grid max-h-[70vh] gap-5 overflow-y-auto pr-1 md:grid-cols-2">
          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-200 p-3 text-sm text-zinc-700">
              <p className="flex items-center gap-2 font-medium text-zinc-900">
                <Film className="size-4" /> MP4 · H.264 · AAC
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                {formatTimecode(duration)} · {doc.timeline?.fps ?? 30} Bilder/s · Lautheit auf −14
                LUFS angeglichen (Instagram, TikTok, YouTube)
              </p>
            </div>
            <label className="block space-y-1">
              <span className="text-xs text-zinc-500">Auflösung</span>
              <Segmented
                className="w-full"
                value={resolution}
                onChange={setResolution}
                options={[
                  {
                    value: "1080p",
                    label: `${sizeLabel("1080p")} (Full HD)`,
                    title: "Volle Auflösung für Instagram, TikTok, YouTube und LinkedIn",
                  },
                  {
                    value: "720p",
                    label: sizeLabel("720p"),
                    title: "Kleinere Datei, schneller fertig",
                  },
                ]}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-zinc-500">Qualität</span>
              <Segmented
                className="w-full"
                value={quality}
                onChange={setQuality}
                options={[
                  { value: "standard", label: "Standard", title: "Gute Qualität, kleinere Datei" },
                  { value: "high", label: "Hoch", title: "Höhere Bitrate, größere Datei" },
                ]}
              />
            </label>
            {captionsElement ? (
              <Toggle
                checked={captions}
                onChange={setCaptions}
                label="Untertitel ins Video einbrennen"
                description={
                  recognising
                    ? "Die Spracherkennung läuft noch – warte kurz, sonst fehlen Untertitel."
                    : `${pages.length} Untertitel`
                }
              />
            ) : null}
            <div className="rounded-lg border border-zinc-200 p-3">
              <p className="text-sm font-medium text-zinc-900">Untertitel als Datei</p>
              <p className="mt-0.5 text-xs text-zinc-500">
                Für YouTube, LinkedIn oder Facebook, die Untertitel selbst einblenden.
              </p>
              <div className="mt-2 flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pages.length === 0}
                  onClick={() =>
                    download(captionsToSrt(pages, uppercase), `${base}.srt`, "application/x-subrip")
                  }
                >
                  <FileText /> SRT
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pages.length === 0}
                  onClick={() =>
                    download(captionsToVtt(pages, uppercase), `${base}.vtt`, "text/vtt")
                  }
                >
                  <FileText /> WebVTT
                </Button>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            {current ? <ExportStatus info={current} /> : null}
            {current?.status === "done" && current.url ? (
              // biome-ignore lint/a11y/useMediaCaption: preview of the user's own export
              <video
                src={current.url}
                controls
                playsInline
                className="mx-auto max-h-80 rounded-lg bg-black"
              />
            ) : null}
            <Button
              className="w-full"
              size="lg"
              loading={
                create.isPending ||
                (current ? current.status === "queued" || current.status === "processing" : false)
              }
              disabled={
                !env.canExport ||
                !plan.data ||
                free ||
                (doc.timeline?.clips.length ?? 0) +
                  (doc.timeline?.overlays?.length ?? 0) +
                  Object.keys(doc.elements).length ===
                  0
              }
              onClick={async () => {
                await beforeExport();
                create.mutate({
                  orgId: env.orgId,
                  documentId: env.documentId,
                  format: "mp4",
                  options: { resolution, quality, captions: captions && Boolean(captionsElement) },
                });
              }}
            >
              <Download /> Video erstellen
            </Button>
            <p className="text-xs text-zinc-500">
              Das Video wird auf dem Server aus den Originaldateien gerendert. Das dauert je nach
              Länge ein paar Minuten – du kannst währenddessen weiterarbeiten.
            </p>
            {!env.canExport ? <NoExportHint orgId={env.orgId} /> : null}
            {recent.data?.some((entry) => entry.status === "done" && entry.url) ? (
              <div>
                <h4 className="mb-1.5 text-xs font-semibold text-zinc-700">Letzte Exporte</h4>
                <ul className="space-y-1">
                  {recent.data
                    .filter((entry) => entry.status === "done" && entry.url)
                    .slice(0, 5)
                    .map((entry) => (
                      <li
                        key={entry.id}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span className="min-w-0 truncate text-zinc-700">{entry.fileName}</span>
                        <span className="flex shrink-0 items-center gap-2 text-xs text-zinc-400">
                          {formatRelative(entry.createdAt)}
                          <a
                            href={entry.url!}
                            download={entry.fileName ?? undefined}
                            className="text-brand-700 hover:underline"
                          >
                            Laden
                          </a>
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function safeName(title: string): string {
  return (
    title
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-zA-Z0-9._-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "untertitel"
  );
}

function DesignExportDialog({ open, onOpenChange, beforeExport }: ExportDialogProps) {
  const env = useEnv();
  const plan = useQuery(orpc.plan.get.queryOptions({ input: { orgId: env.orgId } }));
  const free = plan.data?.plan === "free";
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const isPrint = Boolean(doc.format.print);
  const [format, setFormat] = useState<ExportFormat>(isPrint ? "pdf-print" : "png");
  const [pages, setPages] = useState<"all" | "current">("all");
  const [scale, setScale] = useState(isPrint ? 300 / 96 : 1);
  const [quality, setQuality] = useState(90);
  const freeScale = Math.max(0.25, Math.min(1, 2048 / maxPageEdge(doc)));
  useEffect(() => {
    if (free) {
      setFormat("png");
      setScale(freeScale);
    }
  }, [free, freeScale]);
  // Pages without a background (mockups made from the media library) stay transparent.
  const [transparent, setTransparent] = useState(() =>
    doc.pages.every(
      (page) => typeof page.background === "string" && parseHex(page.background).a === 0,
    ),
  );
  const print = doc.format.print;
  const provider = getPrintProvider(print?.provider);
  const [pdfx, setPdfx] = useState<PdfxStandard>(provider.pdfx);
  const [cropMarks, setCropMarks] = useState(provider.cropMarks);
  const [richBlack, setRichBlack] = useState(false);
  const [overprintBlack, setOverprintBlack] = useState(provider.overprintBlack);
  const { current, recent, started, reset } = useCurrentExport(open, format, 1000);

  const issues = useMemo(() => {
    if (format !== "pdf-print" || !print) return [];
    const assets = Object.fromEntries(
      [...env.assets.values()].map((asset) => [
        asset.id,
        { width: asset.width, height: asset.height, name: asset.name },
      ]),
    );
    const all = preflightDocument(doc, assets, {
      provider: print.provider,
      bleedMm: print.bleedMm,
      safeMm: print.safeMm,
      pdfx,
    });
    return pages === "current"
      ? all.filter((issue) => issue.pageIndex === undefined || issue.pageIndex === pageIndex)
      : all;
  }, [doc, env.assets, format, pages, pageIndex, pdfx, print]);
  const errors = issues.filter((issue) => issue.severity === "error").length;

  const create = useMutation(
    orpc.exports.create.mutationOptions({
      onSuccess: (info) => started(info.id),
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );

  const start = async () => {
    await beforeExport();
    const pageList = pages === "current" ? [pageIndex] : [];
    const base = { orgId: env.orgId, documentId: env.documentId };
    if (format === "png" || format === "jpg") {
      create.mutate({
        ...base,
        format,
        options: { pages: pageList, scale, quality, transparent: format === "png" && transparent },
      });
    } else if (format === "pdf-digital") {
      create.mutate({ ...base, format, options: { pages: pageList } });
    } else if (format === "pdf-print" && print) {
      create.mutate({
        ...base,
        format,
        options: {
          pages: pageList,
          provider: provider.id,
          profile: resolvePrintProfile(print).id,
          bleedMm: print.bleedMm,
          pdfx,
          cropMarks,
          richBlack,
          overprintBlack,
        },
      });
    }
  };

  const exportSize = pageSize(doc, doc.pages[pageIndex]);
  const pixelSize =
    hasMixedSizes(doc) && pages === "all"
      ? "jede Seite in ihrer Größe"
      : `${Math.round(exportSize.width * scale)} × ${Math.round(exportSize.height * scale)} px`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent title="Exportieren" className="max-w-2xl">
        {free ? (
          <p className="mb-4 text-sm text-zinc-600">
            Free: {plan.data?.usage.exportPages ?? 0} von 10 Exportseiten diesen Monat · PNG/JPG bis
            2.048 px. Am 1. beginnt das Kontingent neu.{" "}
            <a className="underline" href={`/o/${env.orgId}/settings?tab=plan`}>
              PDF und MP4 mit Solo freischalten
            </a>
          </p>
        ) : null}
        <div className="grid max-h-[70vh] gap-5 overflow-y-auto pr-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {FORMAT_OPTIONS.filter((option) => option.value !== "pdf-print" || isPrint).map(
                (option) => (
                  <button
                    key={option.value}
                    type="button"
                    disabled={free && option.value !== "png" && option.value !== "jpg"}
                    onClick={() => {
                      setFormat(option.value);
                      reset();
                    }}
                    className={cn(
                      "flex flex-col items-start gap-1 rounded-lg border p-2.5 text-left transition [&_svg]:size-4",
                      format === option.value
                        ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500"
                        : "border-zinc-200 hover:border-zinc-300",
                    )}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-medium text-zinc-900">
                      {option.icon} {option.label}
                      {free && option.value !== "png" && option.value !== "jpg" ? " · Solo" : ""}
                    </span>
                    <span className="text-xs text-zinc-500">{option.description}</span>
                  </button>
                ),
              )}
            </div>
            {doc.pages.length > 1 ? (
              <Segmented
                className="w-full"
                value={pages}
                onChange={setPages}
                options={[
                  { value: "all", label: `Alle ${doc.pages.length} Seiten`, title: "Alle Seiten" },
                  {
                    value: "current",
                    label: `Nur Seite ${pageIndex + 1}`,
                    title: "Aktuelle Seite",
                  },
                ]}
              />
            ) : null}
            {format === "png" || format === "jpg" ? (
              <div className="space-y-2">
                <label className="block space-y-1">
                  <span className="text-xs text-zinc-500">Größe</span>
                  <Select
                    className="h-9"
                    value={String(scale)}
                    onChange={(event) => setScale(Number(event.target.value))}
                  >
                    {free ? (
                      <option value={freeScale}>Free · bis 2.048 px</option>
                    ) : isPrint ? (
                      <>
                        <option value={150 / 96}>150 dpi (Bildschirm)</option>
                        <option value={300 / 96}>300 dpi (Druckqualität)</option>
                      </>
                    ) : (
                      <>
                        <option value={1}>Originalgröße (1×)</option>
                        <option value={2}>Doppelte Größe (2×)</option>
                        <option value={0.5}>Halbe Größe (0,5×)</option>
                      </>
                    )}
                  </Select>
                </label>
                <p className="text-xs text-zinc-500">
                  {pixelSize}
                  {doc.pages.length > 1 && pages === "all" ? " · mehrere Seiten als ZIP" : ""}
                </p>
                {format === "png" ? (
                  <Toggle
                    checked={transparent}
                    onChange={setTransparent}
                    label="Transparenter Hintergrund"
                    description="Ohne Seitenhintergrund – ideal für Mockups, Logos und Sticker."
                  />
                ) : null}
                {format === "jpg" ? (
                  <label className="block space-y-1">
                    <span className="text-xs text-zinc-500">Qualität: {quality} %</span>
                    <input
                      type="range"
                      min={60}
                      max={100}
                      value={quality}
                      onChange={(event) => setQuality(Number(event.target.value))}
                      className="w-full accent-brand-600"
                    />
                  </label>
                ) : null}
              </div>
            ) : null}
            {format === "pdf-print" ? (
              <div className="rounded-lg border border-zinc-200">
                <PrintSettings compact />
                <div className="space-y-2.5 px-4 pb-4">
                  <label className="block space-y-1">
                    <span className="text-xs text-zinc-500">PDF-Standard</span>
                    <Select
                      className="h-8"
                      value={pdfx}
                      onChange={(event) => setPdfx(event.target.value as PdfxStandard)}
                    >
                      {PDFX_STANDARDS.map((standard) => (
                        <option key={standard} value={standard}>
                          {PDFX_LABELS[standard]}
                        </option>
                      ))}
                    </Select>
                  </label>
                  <Toggle
                    checked={cropMarks}
                    onChange={setCropMarks}
                    label="Schnittmarken"
                    description="Nur wenn die Druckerei sie verlangt (Online-Druckereien meist nicht)."
                  />
                  <Toggle
                    checked={overprintBlack}
                    onChange={setOverprintBlack}
                    label="Schwarzen Text überdrucken"
                    description="Verhindert weiße Blitzer bei schwarzem Text auf Farbflächen."
                  />
                  <Toggle
                    checked={richBlack}
                    onChange={setRichBlack}
                    label="Tiefschwarz für große Flächen"
                    description="40/30/30/100 statt 100 % K, Text bleibt reines Schwarz."
                  />
                </div>
              </div>
            ) : null}
          </div>
          <div className="space-y-4">
            {format === "pdf-print" ? (
              <div>
                <h4 className="mb-2 text-sm font-semibold text-zinc-900">Druckprüfung</h4>
                <IssueList
                  issues={issues}
                  onSelect={(issue) => {
                    if (issue.pageIndex !== undefined) store.getState().goToPage(issue.pageIndex);
                    if (issue.elementId) store.getState().select([issue.elementId]);
                    onOpenChange(false);
                  }}
                />
                <p className="mt-2 text-xs text-zinc-400">
                  Nach dem Export prüft der Server zusätzlich Farbräume, Schriften und Farbauftrag
                  im fertigen PDF.
                </p>
              </div>
            ) : null}
            {current ? <ExportStatus info={current} /> : null}
            <Button
              className="w-full"
              size="lg"
              loading={
                create.isPending ||
                (current ? current.status === "queued" || current.status === "processing" : false)
              }
              disabled={
                !env.canExport || !plan.data || (free && format !== "png" && format !== "jpg")
              }
              onClick={() => void start()}
            >
              <Download /> {format === "pdf-print" ? "Druck-PDF erstellen" : "Exportieren"}
            </Button>
            {errors > 0 && format === "pdf-print" ? (
              <p className="text-xs text-red-600">
                Es gibt {errors} Fehler. Du kannst trotzdem exportieren, die Druckerei könnte die
                Datei aber ablehnen.
              </p>
            ) : null}
            {!env.canExport ? <NoExportHint orgId={env.orgId} /> : null}
            {recent.data && recent.data.filter((entry) => entry.status === "done").length > 0 ? (
              <div>
                <h4 className="mb-1.5 text-xs font-semibold text-zinc-700">Letzte Exporte</h4>
                <ul className="space-y-1">
                  {recent.data
                    .filter((entry) => entry.status === "done" && entry.url)
                    .slice(0, 5)
                    .map((entry) => (
                      <li
                        key={entry.id}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span className="min-w-0 truncate text-zinc-700">{entry.fileName}</span>
                        <span className="flex shrink-0 items-center gap-2 text-xs text-zinc-400">
                          {formatRelative(entry.createdAt)}
                          <a
                            href={entry.url!}
                            download={entry.fileName ?? undefined}
                            className="text-brand-700 hover:underline"
                          >
                            Laden
                          </a>
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
